import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { FaPlus, FaEdit, FaTrash, FaArrowLeft, FaSearch } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const navigate = useNavigate();
  const password = localStorage.getItem('adminPassword');

  useEffect(() => {
    if (!password) {
      navigate('/admin');
      return;
    }
    fetchProducts();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [products, searchQuery, selectedCategory]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = [...products];
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }
    if (searchQuery) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    setFilteredProducts(filtered);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${API}/products/${productId}`, {
        data: { password }
      });
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  return (
    <div className='min-h-screen bg-gray-50'>
      {/* Header */}
      <div className='bg-[#0F5132] text-white py-6 shadow-lg'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center space-x-4'>
              <Link to='/admin/dashboard' className='text-white hover:text-gray-200'>
                <FaArrowLeft size={24} />
              </Link>
              <div>
                <h1 className='text-3xl font-bold'>Products Management</h1>
                <p className='text-gray-200 mt-1'>Total: {products.length} products</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {/* Filters */}
        <div className='bg-white rounded-xl shadow-lg p-6 mb-6'>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <div className='md:col-span-2 relative'>
              <FaSearch className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400' />
              <Input
                type='text'
                placeholder='Search products...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='pl-10 w-full'
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className='px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F5132]'
            >
              <option value='All'>All Categories</option>
              <option value='Herbal'>Herbal</option>
              <option value='Nutraceutical'>Nutraceutical</option>
            </select>
          </div>
        </div>

        {/* Results */}
        <div className='mb-6 text-gray-600'>
          Showing <span className='font-semibold text-[#0F5132]'>{filteredProducts.length}</span> of {products.length} products
        </div>

        {/* Products Table */}
        {loading ? (
          <div className='flex justify-center py-20'>
            <div className='animate-spin rounded-full h-12 w-12 border-4 border-[#0F5132] border-t-transparent'></div>
          </div>
        ) : (
          <div className='bg-white rounded-xl shadow-lg overflow-hidden'>
            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-gray-200'>
                <thead className='bg-[#0F5132]'>
                  <tr>
                    <th className='px-6 py-3 text-left text-xs font-bold text-white uppercase tracking-wider'>
                      Product Name
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-bold text-white uppercase tracking-wider'>
                      Category
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-bold text-white uppercase tracking-wider'>
                      Subcategory
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-bold text-white uppercase tracking-wider'>
                      Packaging
                    </th>
                    <th className='px-6 py-3 text-right text-xs font-bold text-white uppercase tracking-wider'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-gray-200'>
                  {filteredProducts.map((product) => (
                    <tr key={product.id} className='hover:bg-gray-50'>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div className='text-sm font-medium text-gray-900'>{product.name}</div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          product.category === 'Herbal' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {product.category}
                        </span>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                        {product.subcategory}
                      </td>
                      <td className='px-6 py-4 text-sm text-gray-500'>
                        {product.packaging_sizes.slice(0, 2).join(', ')}
                        {product.packaging_sizes.length > 2 && '...'}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
                        <div className='flex justify-end space-x-2'>
                          <Link
                            to={`/products/${product.id}`}
                            target='_blank'
                            className='text-blue-600 hover:text-blue-900'
                          >
                            View
                          </Link>
                          <button
                            onClick={() => handleDelete(product.id)}
                            className='text-red-600 hover:text-red-900'
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProducts;
